// services/chat.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';

/**
 * Servicio para gestionar Chat y Conversaciones
 */
class ChatService {
  /**
   * Crear una nueva sesión de chat (Chat History)
   * Versión híbrida: intenta nueva API account-based, fallback a la actual
   */
  async createChatHistory({ accountId, userId, assistantId, title, userTimezone, initialMessage }) {
    // Intentar nueva API account-based primero
    if (accountId) {
      try {
        const requestBody = {
          userTimezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          assistantId: assistantId || null,
          title: title || null,
          externalId: null,
          contextVariables: {},
          systemPrompt: null,
          initialMessage: initialMessage || null
        };

        const response = await assistantClient.post(`/accounts/${accountId}/chat/histories`, requestBody);
        return { success: true, data: response.data };
      } catch (error) {
        // Si es 404, la nueva API no está disponible, usar fallback
        if (error.response?.status === 404) {
          console.warn('Nueva API de chat no disponible, usando API legacy');
        } else {
          // Si es otro error, reportarlo
          return { success: false, error: handleApiError(error) };
        }
      }
    }

    // Fallback a API actual
    if (userId) {
      try {
        const response = await assistantClient.post('/chat/histories', {
          user_id: userId,
          assistant_id: assistantId,
          title: title || undefined,
          user_timezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }

    return { success: false, error: { message: 'accountId o userId requerido' } };
  }

  /**
   * Listar chat histories
   * Versión híbrida: intenta nueva API account-based, fallback a la actual
   */
  async listChatHistories({ accountId, userId, assistantId, projectId, limit = 10, offset = 0, skip = 0, sortBy = 'updated_at', descending = true }) {
    // Intentar nueva API account-based primero
    if (accountId) {
      try {
        const params = {};
        if (assistantId) params.assistant_id = assistantId;
        if (projectId) params.project_id = projectId;
        params.limit = limit;
        params.offset = offset;
        params.sort_by = sortBy;
        params.descending = descending;

        const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, { params });
        return {
          success: true,
          data: response.data.chatHistories || response.data || [],
          total: response.data.total || 0,
        };
      } catch (error) {
        // Si es 404, la nueva API no está disponible, usar fallback
        if (error.response?.status === 404) {
          console.warn('Nueva API de chat no disponible, usando API legacy');
        } else {
          // Si es otro error, reportarlo
          return { success: false, error: handleApiError(error) };
        }
      }
    }

    // Fallback a API actual
    if (userId) {
      try {
        const params = {};
        if (userId) params.user_id = userId;
        if (assistantId) params.assistant_id = assistantId;
        params.limit = limit;
        params.skip = skip;

        const response = await assistantClient.get('/chat/histories', { params });
        return {
          success: true,
          data: response.data.chatHistories || [],
          total: response.data.total || 0,
        };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }

    return { success: false, error: { message: 'accountId o userId requerido' } };
  }

  /**
   * Obtener chat history por ID
   */
  async getChatHistory(chatHistoryId) {
    try {
      const response = await assistantClient.get(`/chat/histories/${chatHistoryId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Enviar mensaje y obtener respuesta del assistant
   */
  async sendMessage({ assistantId, chatHistoryId, message, contentType = 'text', userId, accountId }) {
    try {
      const contentBlocks = [
        {
          type: contentType,
          data: contentType === 'text' ? { text: message } : { url: message },
          order: 1,
        },
      ];

      const requestBody = {
        assistant_id: assistantId,
        chat_history_id: chatHistoryId,
        content_blocks: contentBlocks,
      };

      // Agregar userId y accountId si están disponibles (requeridos por la nueva API)
      if (userId) {
        requestBody.userId = userId;
      }
      if (accountId) {
        requestBody.accountId = accountId;
      }

      const response = await assistantClient.post('/chat/assistant', requestBody);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener mensajes de un chat history
   */
  async getChatMessages(chatHistoryId, { limit = 50, skip = 0 } = {}) {
    try {
      const response = await assistantClient.get(
        `/chat/histories/${chatHistoryId}/messages`,
        { params: { limit, skip } }
      );
      return {
        success: true,
        data: response.data.messages || [],
        total: response.data.total || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener mensajes recientes de un chat
   */
  async getRecentMessages(chatHistoryId, count = 10) {
    try {
      const response = await assistantClient.get(
        `/chat/histories/${chatHistoryId}/messages/recent`,
        { params: { count } }
      );
      return {
        success: true,
        data: response.data.messages || [],
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar título del chat history
   */
  async updateChatHistoryTitle(chatHistoryId, title) {
    try {
      const response = await assistantClient.patch(`/chat/histories/${chatHistoryId}`, {
        title,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar chat history
   */
  async deleteChatHistory(chatHistoryId) {
    try {
      const response = await assistantClient.delete(`/chat/histories/${chatHistoryId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Crear chat history y enviar primer mensaje
   * Esta función combina la creación del chat history con el envío del primer mensaje
   */
  async createChatHistoryWithMessage({ accountId, userId, assistantId, message, contentType = 'text', userTimezone }) {
    try {
      // Crear el chat history con el mensaje inicial para generar el título
      const chatResult = await this.createChatHistory({
        accountId,
        userId,
        assistantId,
        initialMessage: contentType === 'text' ? message : null,
        userTimezone
      });

      if (!chatResult.success) {
        return chatResult;
      }

      const chatHistoryId = chatResult.data.id;

      // Enviar el primer mensaje
      const messageResult = await this.sendMessage({
        assistantId,
        chatHistoryId,
        message,
        contentType,
        userId,
        accountId
      });

      if (messageResult.success) {
        return {
          success: true,
          data: {
            chatHistory: chatResult.data,
            firstMessage: messageResult.data,
            chatHistoryId
          }
        };
      } else {
        // Si falló el mensaje, intentar limpiar el chat history
        await this.deleteChatHistory(chatHistoryId);
        return messageResult;
      }
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

export default new ChatService();
